import type { PropsWithChildren } from 'react';
import { useEffect, useRef } from 'react';
import { styled } from 'styled-components';
import { TRANSFER_ANIMATIONS } from './transfer-animations';

type Props = PropsWithChildren<{
  /**
   * should the reveal animation be played. Usually you'd want
   * to limit this to thumbnail-generating -> preparing / complete
   * stages.
   */
  shouldPlayRevealAnimation: boolean;
  /** should the thumbnail be visible */
  isThumbVisible: boolean;
  /** Is the asset still being prepared? */
  isPreparing?: boolean;
}>;

/**
 * Handles animating the thumbnail itself, in particular
 * 1. Revealing the thumbnail with a circular reveal
 * 2. Pulsing the thumbnail during the preparing state.
 */
export function ThumbnailReveal({
  isPreparing,
  shouldPlayRevealAnimation,
  isThumbVisible,
  children,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  /**
   * Reveal animation handler
   */

  useEffect(() => {
    if (shouldPlayRevealAnimation) {
      reveal(ref.current!);
    }
  }, [shouldPlayRevealAnimation]);

  /**
   * Preparing animation handler
   */

  const wasPreparingRef = useRef(isPreparing);
  const fadeInOutAnimationRef = useRef<Animation>(undefined);

  useEffect(() => {
    const el = ref.current!;

    /**
     * When transitioning to a preparing state, we want to pulse the
     * image in and out.
     */
    if (isPreparing && !fadeInOutAnimationRef.current) {
      fadeInOutAnimationRef.current = fadeInOut(el);
      // When preparing is finished, we want to animate to full opacity
    } else if (!isPreparing && wasPreparingRef.current) {
      // commit the existing styles to seamless animate between the existing
      // opacity and the new one.
      if (fadeInOutAnimationRef.current) {
        fadeInOutAnimationRef.current.commitStyles();
        fadeInOutAnimationRef.current.cancel();
        fadeInOutAnimationRef.current = undefined;
      }

      fadeIn(el);
    }

    wasPreparingRef.current = isPreparing;
  }, [isPreparing]);

  const opacity = isThumbVisible ? 1 : 0;

  return (
    <StyledClipPathContainer
      ref={ref}
      style={{
        opacity,
      }}
    >
      {children}
    </StyledClipPathContainer>
  );
}

const StyledClipPathContainer = styled.div`
  clip-path: none;
  inset: 0;
  position: absolute;
`;

/**
 * Perform a reveal animation on an element using
 * CSS clip-path.
 *
 * @example
 * reveal(document.querySelector('.thumbnail'));
 */

function reveal(element: HTMLElement) {
  const bounds = element.getBoundingClientRect();
  const maxMaskRadius = Math.ceil(
    Math.hypot(bounds.width / 2, bounds.height / 2)
  );

  // eslint-disable-next-line no-param-reassign
  element.animate(
    [{ clipPath: 'circle(16px)' }, { clipPath: `circle(${maxMaskRadius}px)` }],
    {
      duration: TRANSFER_ANIMATIONS.preparing.duration,
      easing: TRANSFER_ANIMATIONS.preparing.ease,
    }
  ).onfinish = () => {
    // unset clippath when finished to reduce performance impact
    if (element) {
      // eslint-disable-next-line no-param-reassign
      element.style.clipPath = 'none';
    }
  };
}

/**
 * Keyframe in / out opacity animation. Use the returned animation
 * to cancel or commit the styles.
 *
 * @example fadeInOut(document.querySelector('.thumbnail')
 */
function fadeInOut(element: HTMLElement) {
  return element.animate(
    {
      opacity: [0.4, 1, 0.4],
    },
    {
      duration: 2000,
      easing: 'ease-in-out',
      iterations: Infinity,
      fill: 'both',
    }
  );
}

/**
 * Fully fade in an element from its existing opacity.
 *
 * @example fadeIn(document.querySelector('.thumbnail')
 */
function fadeIn(element: HTMLElement) {
  const animation = element.animate(
    {
      opacity: 1,
    },
    {
      duration: TRANSFER_ANIMATIONS.duration,
      easing: TRANSFER_ANIMATIONS.easeAsBezierString,
    }
  );

  animation.onfinish = () => {
    if (element) {
      // eslint-disable-next-line no-param-reassign
      element.style.opacity = '1';
    }
  };

  return animation;
}
